import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import {
  getAllDataPoints,
  syncOneDatapoint,
} from "../../indexDB/utils/datapoint";
import { getDatasetByUUID } from "../../indexDB/utils/dataset";
import { getProjectByUUID } from "../../indexDB/utils/project";
import { useNavigate } from "react-router-dom";
import { db } from "../../indexDB/model";
import { handleApiError } from "../errorBoundary/errorUtils";
import { useErrorBoundary } from "react-error-boundary";
import {getAllDrafts} from "../../indexDB/utils/draft";
import { deleteDraftbyId } from "../../indexDB/utils/draft";

/**
 * Component for managing and syncing unsynced datapoints.
 *
 * @component
 *
 * @param {Object} props - Component properties.
 * @param {Function} props.handleLogout - Logout function.
 * @returns {JSX.Element} - UnsyncDatapoints component.
 */
const Drafts = ({ handleLogout }) => {
  const { showBoundary } = useErrorBoundary();

  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const rowsPerPage = Math.floor(window.innerHeight / 100);

  /**
   * Fetches unsynced datapoints from IndexedDB and updates state.
   *
   * @returns {void}
   */
  const fetchEntriesFromIndexedDB = async () => {
    try {
      const entries = await getAllDrafts();
      const entriesWithData = await Promise.all(
        entries.map(async (entry) => {
          const draftId = entry.draftId;
          const formData = entry.formData;
          const fileData = entry.fileData;
          const datasetId = entry.datasetId
          const dataset = await getDatasetByUUID(datasetId);
          const project = dataset ? await getProjectByUUID(dataset.projectID) : null;
          const draftModifiedTimestamp = entry.timestamp || null;


          return {
            draftId: draftId,
            formData: formData,
            fileData: fileData,
            datasetId: datasetId,
            dataset: dataset,
            project: project,
            showLabel1: formData?.sample_number || "----",
            showLabel2: formData?.category_name || "----",
            collapsibleSections: entry?.collapsibleSections,
            draftDatetime: draftModifiedTimestamp
          };
        })
      );
      console.log(entriesWithData)
      setEntries(entriesWithData);
    } 
    finally{
      setTimeout(() => {
        setLoading(false);
      }, 1000);

    }
  };



  useEffect(() => {
    fetchEntriesFromIndexedDB();
  }, []);

  /**
   * Handles syncing all unsynced datapoints.
   *
   * @returns {void}
   */

  /**
   * Handles syncing a single unsynced datapoint by ID.
   *
   * @param {number} id - ID of the datapoint to sync.
   * @returns {void}
   */


  /**
   * Handles clicking on an item and navigates to view the form.
   *
   * @param {string} datasetID - ID of the dataset.
   * @param {Object} formData - Form data object.
   * @param {number} id - ID of the datapoint.
   * @returns {void}
   */
  const resumeDraft = async (draftId,formData,fileData,datasetId,dataset,project,collapsibleSections,draftDatetime) => {
    navigate("/resume-draft", {state:{draftId,formData,fileData,datasetId,dataset,project,collapsibleSections,draftDatetime}});
  };

  const deleteDraft = async (draftId)=> {
    const draft = await deleteDraftbyId(draftId);
    const modefiedEntries =  entries.filter(item => !(item.draftId == draftId))
    setEntries(modefiedEntries)
  }

  return (
    <div>
      <Container>
        <Container style={{ padding: 4, marginTop: 16 }}>
          <Divider />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Available Drafts
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Dataset Name</TableCell>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Identifier 1</TableCell>
                  <TableCell>Identifier 2</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: rowsPerPage }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 6 }).map((_, colIndex) => (
                          <TableCell key={colIndex}>
                            <Skeleton animation="wave" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : entries?.map((entry, index) => (
                      <TableRow
                        key={index}
                        hover
                        style={
                          entry.isRemoving
                            ? { opacity: 0, transition: "opacity 0.5s" }
                            : null
                        }
                      >
                        <TableCell>{entry.draftId}</TableCell>
                        <TableCell>{entry.dataset?.name || "-----"}</TableCell>
                        <TableCell>{entry.project?.name || "-----"}</TableCell>
                        <TableCell>{entry.showLabel1 || "-----"}</TableCell>
                        <TableCell>{entry.showLabel2 || "-----"}</TableCell>
                        <TableCell>
                          <ButtonGroup>
                          <Button
                              onClick={() =>
                                resumeDraft(
                                  entry.draftId,
                                  entry.formData,
                                  entry.fileData,
                                  entry.datasetId,
                                  entry.dataset,
                                  entry.project,
                                  entry.collapsibleSections,
                                  entry.draftDatetime
                                )
                              }
                            >
                              Resume
                            </Button>
                          <Button onClick={()=>{deleteDraft(entry.draftId)}}>
                              Delete
                          </Button>
                          </ButtonGroup>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Container>
    </div>
  );
};

export default Drafts;
